import { useState, useEffect } from 'react'
import {
  Typography,
  Box,
  Card,
  CardContent,
  FormGroup,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Stack
} from '@mui/material'
import {
  Save as SaveIcon,
  Security as SecurityIcon,
  Download as DownloadIcon,
  Lock as LockIcon
} from '@mui/icons-material'
import { userService } from '../../../../shared/services/UserService'
import { UserPreferences } from '../../../../shared/types/entities'
import { mfaService } from '../../../../shared/services/MfaService'
import { apiClient } from '../../../../shared/services/ApiClient'
import PasswordStrengthMeter from '../../components/PasswordStrengthMeter'

function CustomerProfile() {
  const [preferences, setPreferences] = useState<UserPreferences | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // MFA state
  const [mfaEnabled, setMfaEnabled] = useState(false)
  const [mfaLoading, setMfaLoading] = useState(false)
  const [showEnrollDialog, setShowEnrollDialog] = useState(false)
  const [enrollmentStep, setEnrollmentStep] = useState<'qr' | 'verify' | 'recovery'>('qr')
  const [qrCodeUrl, setQrCodeUrl] = useState('')
  const [mfaSecret, setMfaSecret] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([])
  const [showDisableDialog, setShowDisableDialog] = useState(false)
  const [disablePassword, setDisablePassword] = useState('')
  const [remainingRecoveryCodes, setRemainingRecoveryCodes] = useState(0)

  // Change password state
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordChanging, setPasswordChanging] = useState(false)

  // Data export state
  const [exporting, setExporting] = useState(false)

  useEffect(() => {
    loadPreferences()
    loadMfaStatus()
  }, [])

  const loadPreferences = async () => {
    try {
      setLoading(true)
      setError(null)
      const prefs = await userService.getMyPreferences()
      setPreferences(prefs)
    } catch (err: any) {
      setError(err.message || 'Failed to load preferences')
      // Set default preferences if loading fails
      setPreferences({
        emailNotifications: {
          ticketCreated: true,
          ticketUpdated: true,
          ticketClosed: true,
          newComment: true,
          ticketAssigned: true,
          ticketEscalated: true,
          ticketMention: true,
          dailyDigest: false
        },
        ui: {
          theme: 'auto',
          ticketsPerPage: 20,
          defaultSort: 'createdAt-desc'
        }
      })
    } finally {
      setLoading(false)
    }
  }

  const handleEmailPreferenceChange = (key: keyof UserPreferences['emailNotifications']) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!preferences) return
    setPreferences({
      ...preferences,
      emailNotifications: {
        ...preferences.emailNotifications,
        [key]: event.target.checked
      }
    })
  }

  const handleSave = async () => {
    if (!preferences) return

    try {
      setSaving(true)
      setError(null)
      setSuccessMessage(null)
      await userService.updateMyPreferences(preferences)
      setSuccessMessage('Preferences saved successfully')
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err: any) {
      setError(err.message || 'Failed to save preferences')
    } finally {
      setSaving(false)
    }
  }

  // MFA functions
  const loadMfaStatus = async () => {
    try {
      const status = await mfaService.getStatus()
      setMfaEnabled(status.mfaEnabled)
      setRemainingRecoveryCodes(status.remainingRecoveryCodes)
    } catch (err: any) {
      console.error('Failed to load MFA status:', err)
    }
  }

  const handleEnableMfa = async () => {
    try {
      setMfaLoading(true)
      setError(null)
      const response = await mfaService.initiateEnrollment()
      setQrCodeUrl(response.qrCodeDataUri)
      setMfaSecret(response.secret)
      setEnrollmentStep('qr')
      setShowEnrollDialog(true)
    } catch (err: any) {
      setError(err.message || 'Failed to initiate MFA enrollment')
    } finally {
      setMfaLoading(false)
    }
  }

  const handleVerifyEnrollment = async () => {
    try {
      setMfaLoading(true)
      setError(null)
      const response = await mfaService.confirmEnrollment(verificationCode)
      setRecoveryCodes(response.recoveryCodes)
      setEnrollmentStep('recovery')
      setMfaEnabled(true)
      setSuccessMessage('MFA enabled successfully!')
    } catch (err: any) {
      setError(err.message || 'Invalid verification code')
    } finally {
      setMfaLoading(false)
    }
  }

  const handleCloseEnrollDialog = () => {
    setShowEnrollDialog(false)
    setEnrollmentStep('qr')
    setVerificationCode('')
    setQrCodeUrl('')
    setMfaSecret('')
    setRecoveryCodes([])
    loadMfaStatus()
  }

  const handleDisableMfa = async () => {
    try {
      setMfaLoading(true)
      setError(null)
      await mfaService.disable(disablePassword)
      setMfaEnabled(false)
      setShowDisableDialog(false)
      setDisablePassword('')
      setSuccessMessage('MFA disabled successfully')
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err: any) {
      setError(err.message || 'Failed to disable MFA')
    } finally {
      setMfaLoading(false)
    }
  }

  const downloadRecoveryCodes = () => {
    const text = recoveryCodes.join('\n')
    const blob = new Blob([text], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'mfa-recovery-codes.txt'
    link.click()
    URL.revokeObjectURL(url)
  }

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match')
      return
    }

    if (newPassword.length < 15) {
      setError('Password must be at least 15 characters long')
      return
    }

    try {
      setPasswordChanging(true)
      setError(null)

      await apiClient.post('/auth/change-password', {
        currentPassword,
        newPassword,
        confirmPassword
      })

      setSuccessMessage('Password changed successfully')
      setShowChangePasswordDialog(false)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err: any) {
      setError(err.message || 'Failed to change password')
    } finally {
      setPasswordChanging(false)
    }
  }

  const handleCloseChangePasswordDialog = () => {
    setShowChangePasswordDialog(false)
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setError(null)
  }

  const handleExportData = async () => {
    try {
      setExporting(true)
      setError(null)

      const data = await apiClient.get<any>('/account/export-data')

      // Create a JSON file and download it
      const jsonData = JSON.stringify(data, null, 2)
      const blob = new Blob([jsonData], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `taylin-helpdesk-data-export-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      setSuccessMessage('Your data has been exported successfully')
      setTimeout(() => setSuccessMessage(null), 5000)
    } catch (err: any) {
      setError(err.message || 'Failed to export data')
    } finally {
      setExporting(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (!preferences) {
    return (
      <Box>
        <Alert severity="error">Failed to load preferences</Alert>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Profile & Settings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Two-Factor Authentication (MFA)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add an extra layer of security to your account
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="body1" fontWeight="bold">
                Status: {mfaEnabled ? <Chip label="Enabled" color="success" size="small" /> : <Chip label="Disabled" color="default" size="small" />}
              </Typography>
              {mfaEnabled && remainingRecoveryCodes > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {remainingRecoveryCodes} recovery code{remainingRecoveryCodes !== 1 ? 's' : ''} remaining
                </Typography>
              )}
            </Box>
            <Button
              variant={mfaEnabled ? 'outlined' : 'contained'}
              color={mfaEnabled ? 'error' : 'primary'}
              onClick={mfaEnabled ? () => setShowDisableDialog(true) : handleEnableMfa}
              disabled={mfaLoading}
              startIcon={mfaLoading ? <CircularProgress size={20} /> : <SecurityIcon />}
            >
              {mfaEnabled ? 'Disable MFA' : 'Enable MFA'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <LockIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Change your account password
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Button
            variant="outlined"
            onClick={() => setShowChangePasswordDialog(true)}
            startIcon={<LockIcon />}
          >
            Change Password
          </Button>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <DownloadIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Data Export
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Download all your data (GDPR Article 20 - Right to Data Portability)
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="body2" sx={{ mb: 2 }}>
            Export includes your profile, tickets, messages, attachments metadata, and activity logs in JSON format.
          </Typography>

          <Button
            variant="outlined"
            onClick={handleExportData}
            disabled={exporting}
            startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
          >
            {exporting ? 'Exporting...' : 'Export My Data'}
          </Button>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Email Notifications
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose which email notifications you want to receive
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.emailNotifications.ticketCreated}
                  onChange={handleEmailPreferenceChange('ticketCreated')}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Ticket Created</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Receive email when you create a new ticket
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={preferences.emailNotifications.ticketUpdated}
                  onChange={handleEmailPreferenceChange('ticketUpdated')}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Ticket Updated</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Receive email when your ticket status changes
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={preferences.emailNotifications.ticketClosed}
                  onChange={handleEmailPreferenceChange('ticketClosed')}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Ticket Closed</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Receive email when your ticket is closed
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={preferences.emailNotifications.newComment}
                  onChange={handleEmailPreferenceChange('newComment')}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">New Comment</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Receive email when someone comments on your ticket
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={preferences.emailNotifications.ticketMention}
                  onChange={handleEmailPreferenceChange('ticketMention')}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Mentions</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Receive email when someone @mentions you in a ticket comment
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={preferences.emailNotifications.ticketEscalated}
                  onChange={handleEmailPreferenceChange('ticketEscalated')}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Ticket Escalated</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Receive email when a ticket is escalated to you (Agents only)
                  </Typography>
                </Box>
              }
            />
          </FormGroup>

          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Preferences'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* MFA Enrollment Dialog */}
      <Dialog open={showEnrollDialog} onClose={handleCloseEnrollDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {enrollmentStep === 'qr' && 'Set Up Two-Factor Authentication'}
          {enrollmentStep === 'verify' && 'Verify Your Code'}
          {enrollmentStep === 'recovery' && 'Save Your Recovery Codes'}
        </DialogTitle>
        <DialogContent>
          {enrollmentStep === 'qr' && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body1" gutterBottom>
                1. Install an authenticator app (Google Authenticator, Authy, etc.)
              </Typography>
              <Typography variant="body1" gutterBottom sx={{ mb: 3 }}>
                2. Scan this QR code with your authenticator app
              </Typography>

              {qrCodeUrl && (
                <Box sx={{ mb: 3 }}>
                  <img src={qrCodeUrl} alt="QR Code" style={{ maxWidth: '250px' }} />
                </Box>
              )}

              <Typography variant="body2" color="text.secondary" gutterBottom>
                Or enter this secret manually:
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mb: 3 }}>
                {mfaSecret}
              </Typography>

              <TextField
                fullWidth
                label="Verification Code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Enter 6-digit code"
                inputProps={{
                  maxLength: 6,
                  pattern: '[0-9]*',
                  inputMode: 'numeric'
                }}
                sx={{ mt: 2 }}
              />
            </Box>
          )}

          {enrollmentStep === 'recovery' && (
            <Box sx={{ py: 2 }}>
              <Alert severity="warning" sx={{ mb: 3 }}>
                Save these recovery codes in a secure location. You can use them to access your account if you lose your device.
              </Alert>

              <Stack spacing={1} sx={{ mb: 3 }}>
                {recoveryCodes.map((code, index) => (
                  <Typography key={index} variant="body2" sx={{ fontFamily: 'monospace', p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                    {code}
                  </Typography>
                ))}
              </Stack>

              <Button
                fullWidth
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={downloadRecoveryCodes}
                sx={{ mb: 2 }}
              >
                Download Recovery Codes
              </Button>
            </Box>
          )}

          {error && enrollmentStep !== 'recovery' && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {enrollmentStep === 'qr' && (
            <>
              <Button onClick={handleCloseEnrollDialog}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleVerifyEnrollment}
                disabled={mfaLoading || verificationCode.length === 0}
              >
                {mfaLoading ? <CircularProgress size={20} /> : 'Verify & Enable'}
              </Button>
            </>
          )}
          {enrollmentStep === 'recovery' && (
            <Button variant="contained" onClick={handleCloseEnrollDialog}>
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showChangePasswordDialog} onClose={handleCloseChangePasswordDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Enter your current password and choose a new password (minimum 15 characters)
          </Typography>
          <Typography variant="body2" color="primary" sx={{ mb: 3, fontStyle: 'italic' }}>
            Example: quiet-mountain-flows-72 (but create your own unique variation!)
          </Typography>

          <TextField
            fullWidth
            type="password"
            label="Current Password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            sx={{ mb: 2 }}
            autoFocus
          />

          <TextField
            fullWidth
            type="password"
            label="New Password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={{ mb: 2 }}
          />
          <PasswordStrengthMeter password={newPassword || ''} />

          <TextField
            fullWidth
            type="password"
            label="Confirm New Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseChangePasswordDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleChangePassword}
            disabled={passwordChanging || !currentPassword || !newPassword || !confirmPassword}
          >
            {passwordChanging ? <CircularProgress size={20} /> : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MFA Disable Dialog */}
      <Dialog open={showDisableDialog} onClose={() => setShowDisableDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter your password to disable MFA. This will make your account less secure.
          </Typography>

          <TextField
            fullWidth
            type="password"
            label="Password"
            value={disablePassword}
            onChange={(e) => setDisablePassword(e.target.value)}
            autoFocus
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDisableDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDisableMfa}
            disabled={mfaLoading || disablePassword.length === 0}
          >
            {mfaLoading ? <CircularProgress size={20} /> : 'Disable MFA'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CustomerProfile
